// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Ícones — SVG de traço único, uma só cor
// ============================================================================
//
// PORQUE NÃO EMOJI
//   A versão anterior usava `✈`, `🚆`, `⛴`, `◆`, `↓`, `↑`. Três problemas, e os
//   três foram apontados pelo cliente:
//
//   1. **Não são de cor única.** O `🚆` e o `⛴` são pictogramas coloridos, e a
//      cor deles não é nossa — logo entra no mapa cor sem significado, quando o
//      sistema de cor é justamente o que dá sentido ao ecrã.
//   2. **Mudam de forma por sistema.** O mesmo emoji é desenhado de maneira
//      diferente no Windows, no Mac e no Android. Um ícone que muda de forma não
//      pode fazer parte de um sistema visual.
//   3. **Não escalam com a tipografia.** Ficam grandes e desalinhados em texto de
//      9 px, que é o tamanho da célula.
//
//   Estes usam `currentColor` e `1em`: herdam a cor e o tamanho do texto onde
//   estão. Um ícone dentro de uma barra escura fica claro sem ninguém o dizer.
//
// PORQUE INLINE E NÃO UMA BIBLIOTECA
//   São nove ícones. Uma dependência para nove caminhos SVG seria mais código a
//   instalar do que a escrever, e amarrava o desenho a decisões de outra pessoa.
//
// ⚠️ `aria-hidden` em todos: o significado está sempre também em texto — no
// tooltip, no cartão de resumo ou no painel. Um ícone anunciado por leitor de
// ecrã em cima do texto que o explica é ruído, não acessibilidade.
//
// UM SÓ `<path>` POR ÍCONE — e a razão é medida
//   A primeira versão usava 2 a 5 elementos por ícone. Os nós por dia subiram de
//   3,7 para 5,9 e o p95 de ~18 para ~29 ms — proporcional, como o *spike* previa.
//   Nas máquinas do escritório (factor ~2,5) isso passaria o limiar de 60 ms que o
//   ADR-001 fixou para reavaliar.
//
//   Um `<path>` aceita **vários sub-caminhos** no mesmo `d`, separados por `M`.
//   Cada ícone é agora `<svg>` + `<path>` = 2 nós, sem perder desenho nenhum.
// ============================================================================

interface Props {
  /** Tamanho em `em`, relativo ao texto onde o ícone está. */
  readonly em?: number;
  readonly className?: string;
}

function Svg({
  em = 1,
  className,
  children,
  traco = true,
}: Props & { children: React.ReactNode; traco?: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      width={`${em}em`}
      height={`${em}em`}
      fill={traco ? 'none' : 'currentColor'}
      stroke={traco ? 'currentColor' : 'none'}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Voo. Requisito V8. */
export function IconeVoo(p: Props) {
  return (
    <Svg {...p} traco={false}>
      <path d="M8.6 1.6c.3-.5 1-.5 1.3 0 .2.3.2.7.1 1L9 6l4.6 2.2c.3.1.5.4.5.8v1.3c0 .4-.4.7-.8.6L8.9 9.6l-.6 3.1 1.5 1.1c.2.2.3.4.3.6v.3c0 .3-.3.5-.6.4l-2.3-.7-2.3.7c-.3.1-.6-.1-.6-.4v-.3c0-.2.1-.5.3-.6l1.5-1.1-.6-3.1-4.4 1.3c-.4.1-.8-.2-.8-.6V9c0-.3.2-.6.5-.8L7 6 6 2.6c-.1-.3-.1-.7.1-1 .3-.5 1-.5 1.3 0" />
    </Svg>
  );
}

/** Comboio (AVE). Requisito V8. */
export function IconeComboio(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3.5 3.5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2z M3.5 6h9 M5 10.5 3 14.5 M11 10.5l2 4 M4.5 13h7" />
    </Svg>
  );
}

/** Ferry ou barco. Requisito V8 — e o art. 9.º do Reg. 561/2006 tem regras próprias. */
export function IconeFerry(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2 9.5h12l-1.4 4.2a1 1 0 0 1-.95.68H4.35a1 1 0 0 1-.95-.68z M4 9.5V5.5h8v4 M8 5.5V2.5" />
    </Svg>
  );
}

/** Chegada. Substitui o `↓`. */
export function IconeChegada(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8 2v8 M4.8 6.8 8 10l3.2-3.2 M2.5 13.5h11" />
    </Svg>
  );
}

/** Partida. Substitui o `↑`. */
export function IconePartida(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8 14V6 M4.8 9.2 8 6l3.2 3.2 M2.5 2.5h11" />
    </Svg>
  );
}

/** Viatura decorada com a imagem de um cliente — restringe quem a pode usar. */
export function IconeDecoracao(p: Props) {
  return (
    <Svg {...p} traco={false}>
      <path d="M8 1.8 9.9 5.7l4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />
    </Svg>
  );
}

/** Viatura indisponível: oficina, avaria, sinistro. */
export function IconeOficina(p: Props) {
  return (
    <Svg {...p}>
      <path d="M10.3 2.2a3.6 3.6 0 0 0-1.1 5.9l-6 6a1.3 1.3 0 0 0 1.8 1.8l6-6a3.6 3.6 0 0 0 4.3-4.8l-2 2-1.6-.3-.3-1.6z" />
    </Svg>
  );
}

/** Dia de rotação: chega de um itinerário e parte noutro. Objectivo O1. */
export function IconeRotacao(p: Props) {
  return (
    <Svg {...p}>
      <path d="M13 6.5A5 5 0 0 0 3.6 4.8 M3 2.5V5h2.5 M3 9.5a5 5 0 0 0 9.4 1.7 M13 13.5V11h-2.5" />
    </Svg>
  );
}

/** Tripulação dupla: dois condutores em simultâneo. Art. 8.º n.º 5. */
export function IconeDupla(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8.2 5A2.2 2.2 0 1 1 3.8 5a2.2 2.2 0 0 1 4.4 0 M2.2 13.2a3.8 3.8 0 0 1 7.6 0 M11 3.2a2.2 2.2 0 0 1 0 4.3 M12.2 9.6a3.8 3.8 0 0 1 1.6 3.6" />
    </Svg>
  );
}

/** Reserva: registo não confirmado. */
export function IconeReserva(p: Props) {
  return (
    <Svg {...p}>
      <path d="M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0 M8 5v3.2l2.2 1.4" />
    </Svg>
  );
}

export function IconeChevron({ aberto = false, ...p }: Props & { aberto?: boolean }) {
  return (
    <Svg {...p}>
      <path d={aberto ? 'M3.5 6.5 8 11l4.5-4.5' : 'M6 3.5 10.5 8 6 12.5'} />
    </Svg>
  );
}

/** Lista de pedidos. Três linhas com marca à esquerda — a fila de trabalho. */
export function IconeLista(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3 4.5h1.5M3 8h1.5M3 11.5h1.5M6.5 4.5H13M6.5 8H13M6.5 11.5H13" />
    </Svg>
  );
}

export function IconeFechar(p: Props) {
  return (
    <Svg {...p}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </Svg>
  );
}

export function IconeVisto(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3 8.5 6.2 12l7-8" />
    </Svg>
  );
}

export function IconeVazio(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2.5 4.5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2z" />
    </Svg>
  );
}

export function IconePaleta(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8 1.8a6.2 6.2 0 1 0 0 12.4c.9 0 1.4-.6 1.4-1.3 0-.8-.6-1.2-.6-1.9 0-.6.5-1.1 1.2-1.1h1.3a2.9 2.9 0 0 0 2.9-2.9C14.2 3.9 11.4 1.8 8 1.8 M5 6h.01 M8 4.5h.01 M5.2 9.4h.01" />
    </Svg>
  );
}

export function IconeInfo(p: Props) {
  return (
    <Svg {...p}>
      <path d="M14.2 8A6.2 6.2 0 1 1 1.8 8a6.2 6.2 0 0 1 12.4 0 M8 7.2v4 M8 4.9h.01" />
    </Svg>
  );
}

export function IconePesquisa(p: Props) {
  return (
    <Svg {...p}>
      <path d="M11.5 7a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0 M10.4 10.4 14 14" />
    </Svg>
  );
}
